import axios from 'axios';

// Determine API URL dynamically
const getAPIURL = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) {
    return process.env.REACT_APP_API_URL;
  }

  const host = window.location.hostname;
  const protocol = window.location.protocol;

  if (host === 'localhost' || host === '127.0.0.1') {
    return `${protocol}//${host}:8000`;
  }

  // Azure Container Apps default domain: ca-<prefix>-web.<env>.<region>
  // .azurecontainerapps.io -> swap in "-api." for the API app. This keeps
  // the API on the SAME registrable domain as the frontend -- the session
  // cookie is SameSite=Lax, sent on same-site requests only, so guessing
  // wrong here (or baking in one fixed URL at build time) silently breaks
  // login from whichever hostname doesn't match.
  if (host.endsWith('.azurecontainerapps.io') && host.includes('-web.')) {
    return `${protocol}//${host.replace('-web.', '-api.')}`;
  }

  // Custom domain convention: <prefix>app.<domain> -> <prefix>api.<domain>,
  // e.g. dev-policyapp.medigovern.com -> dev-policyapi.medigovern.com,
  // policyapp.medigovern.com -> policyapi.medigovern.com. Same same-site
  // reasoning as above.
  if (host.includes('app.')) {
    return `${protocol}//${host.replace('app.', 'api.')}`;
  }

  // Fallback for anything reached directly by IP/hostname with neither
  // naming convention (e.g. a LAN IP hitting a docker-compose host).
  return `${protocol}//${host}:8000`;
};

const API_URL = getAPIURL();
console.log('🔌 API URL:', API_URL);
console.log('🌐 Browser Host:', window.location.host);

/**
 * Interactive request budget.
 *
 * This was 10s, which sits BELOW the floor of a cold start: the API runs with
 * minReplicas=0, so a scale-from-zero is a container start plus a Python boot
 * before a single line of handler code runs. Every request racing that lost,
 * and the raw axios message ("timeout of 10000ms exceeded") went straight into
 * the page's error banner -- usually alongside an unexplained bounce to the
 * login screen, which is what GitHub issue #24 reported.
 *
 * Not 0/absent: a request that hangs forever never surfaces an error at all,
 * which is worse than a slow one. 60s is above the cold-start floor with room.
 */
const DEFAULT_TIMEOUT_MS = 60000;

/**
 * Uploads get their own, much longer budget. A 25MB attachment (the
 * MAX_UPLOAD_MB default) over a slow uplink is legitimately minutes of
 * transfer, and it must not share a budget tuned for "did the server answer".
 */
const UPLOAD_TIMEOUT_MS = 300000;

// Per-request override for the multipart/blob calls below.
const uploadConfig = (config = {}) => ({ ...config, timeout: UPLOAD_TIMEOUT_MS });

// Long-form AI generation gets its own budget, and it is load-bearing rather
// than a nicety: DEFAULT_TIMEOUT_MS is 60s, while drafting a whole policy body
// routinely takes longer. The backend's ai_policy_guide_timeout_seconds is the
// budget for ONE provider call and the orchestrator may make two (one repair
// retry), so this has to exceed twice that plus a cold start.
const AI_GENERATION_TIMEOUT_MS = 240000;
const aiConfig = (config = {}) => ({ ...config, timeout: AI_GENERATION_TIMEOUT_MS });

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: DEFAULT_TIMEOUT_MS,
  // Load-bearing. Identity is an HttpOnly session cookie, and the frontend is
  // served from :3000 while the API lives on :8000 -- a different origin, so
  // axios will NOT attach cookies without this. The backend already sets
  // allow_credentials=True and a non-wildcard CORS origin list, which is what
  // makes it legal.
  withCredentials: true,
});

/**
 * Called when the API says the session is gone. Set by AuthContext.
 *
 * A callback rather than a direct navigate(): this module is imported outside
 * the Router, so useNavigate() is unavailable here and calling history directly
 * would desync react-router's internal state.
 */
let onUnauthorized = null;
export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler;
};

// Requests where a 401 is a normal answer rather than an expired session.
// /auth/me is how we ASK whether anyone is logged in -- treating its 401 as
// "session expired" would fire the handler on every page load for a logged-out
// visitor, and redirect a user who is already at /login back to /login forever.
const AUTH_EXEMPT = ['/api/auth/me', '/api/auth/login', '/api/auth/logout'];

// Add request logging
apiClient.interceptors.request.use(
  config => {
    console.log(`📤 ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  error => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response logging and error handling
apiClient.interceptors.response.use(
  response => {
    console.log(`📥 ${response.status} ${response.config.url}`);
    return response;
  },
  error => {
    const errorInfo = {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
      code: error.code,
    };
    console.error('❌ API Error:', errorInfo);
    
    if (error.code === 'ERR_NETWORK') {
      console.error('🌐 Network Error: Cannot reach API at', API_URL);
      console.error('   Check if backend is running and accessible');
    }

    if (error.code === 'ECONNABORTED') {
      console.error('⏱️  Timed out after', error.config?.timeout, 'ms:', error.config?.url);
      console.error('   The API may be cold-starting (minReplicas=0).');
    }

    // 401 means "no valid session" -- hand off so the app can show the login
    // screen. 403 is deliberately NOT handled here: it means "logged in, but not
    // allowed", and bouncing the user to /login for it would log them out for
    // clicking something they lack permission for. Callers surface the message.
    const url = error.config?.url || '';
    const isExempt = AUTH_EXEMPT.some((path) => url.includes(path));
    if (error.response?.status === 401 && !isExempt && onUnauthorized) {
      onUnauthorized();
    }

    return Promise.reject(error);
  }
);

/**
 * Serialize query params, expanding arrays into repeated bare keys
 * (`policy_area_id=1&policy_area_id=2`) to match FastAPI's `Query(List[...])`.
 * Axios' default array encoding uses `key[]=`, which FastAPI does not parse.
 * Empty strings, null and undefined are dropped so blank filters don't hit the API.
 */
const buildParams = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== null && item !== undefined && item !== '') search.append(key, item);
      });
    } else {
      search.append(key, value);
    }
  });
  return search;
};

// Documents API
export const documentsAPI = {
  list: (params = {}) => apiClient.get('/api/documents/', { params: buildParams(params) }),
  // `context` labels which surface is loading the document, for policy use
  // analytics (issue #62): four pages call this one endpoint, and without it
  // an author opening the edit form is indistinguishable from somebody reading
  // the policy. A hint only -- the backend never authorizes on it.
  get: (id, context) => apiClient.get(`/api/documents/${id}`,
    context ? { params: { context } } : undefined),
  create: (data) => {
    if (data instanceof FormData) {
      return apiClient.post('/api/documents/', data, uploadConfig({
        headers: { 'Content-Type': 'multipart/form-data' }
      }));
    }
    return apiClient.post('/api/documents/', data);
  },
  update: (id, data) => {
    if (data instanceof FormData) {
      return apiClient.put(`/api/documents/${id}`, data, uploadConfig({
        headers: { 'Content-Type': 'multipart/form-data' }
      }));
    }
    return apiClient.put(`/api/documents/${id}`, data);
  },
  delete: (id) => apiClient.delete(`/api/documents/${id}`),
  getHistory: (id) => apiClient.get(`/api/documents/${id}/history`),
  // Existed on the backend and was called from nowhere in the frontend --
  // the view page is the first consumer.
  getWorkflowHistory: (id) => apiClient.get(`/api/documents/${id}/workflow-history`),
  // Issue #60 -- a document owner's own workload, bucketed server-side
  // (in_progress / coming_due / other). Declared as its own method rather
  // than a `list` filter param since the backend returns three lists plus
  // horizon_days in one shape, not a flat array.
  myTasks: () => apiClient.get('/api/documents/my-tasks'),
  // The sidebar badge's numbers -- how many of the four things a My Tasks
  // visit could resolve are outstanding right now. Deliberately a separate,
  // much cheaper call from myTasks() above: that one hydrates and enriches
  // every owned document; this is four small counts. See Sidebar.js for how
  // often it's actually called.
  myTaskCounts: () => apiClient.get('/api/documents/my-tasks/count'),
  // The Start Page's "Ask a question" mode (issue #173). aiConfig()'s longer
  // budget, because this reads several policies and writes an answer.
  //
  // IT NEVER REJECTS FOR A QUOTA REFUSAL. Unlike every other AI call here, the
  // backend answers 200 with `mode_used: "lexical"` and the word-matched
  // results when the caller is out of credits or the feature is unavailable --
  // so there is no QuotaLimitModal on this path and no catch to add for it.
  // See routers/documents.py::search_answer.
  //
  // Plain keyword search deliberately has NO method here: it is documentsAPI
  // .list({ search, status: ['active'], scope }), the same endpoint the
  // Documents page uses, so the two modes cannot disagree about what matches.
  searchAnswer: (data) => apiClient.post('/api/documents/search/answer', data, aiConfig()),
  // No aiConfig(): view counts are a grouped query, not a model call.
  mostReferenced: (params = {}) =>
    apiClient.get('/api/documents/most-referenced', { params: buildParams(params) }),
  // Issue #188 -- the caller's OWN recent reads, unlike mostReferenced above
  // (deliberately aggregate-only). Self-scoped from the session.
  recentlyViewed: (params = {}) =>
    apiClient.get('/api/documents/recently-viewed', { params: buildParams(params) }),
  // Issue #187 -- documents the caller was named a contributor on.
  // Self-scoped, same shape as myTasks/myMentions.
  myContributions: () => apiClient.get('/api/documents/my-contributions'),
  download: (id) => apiClient.get(`/api/documents/${id}/download`, uploadConfig({ responseType: 'blob' })),
  // Not a fetch -- a plain URL for an <a target="_blank"> (GitHub #43). The
  // session cookie is SameSite=Lax, sent on a top-level cross-site GET
  // navigation, so the browser's own PDF viewer can open this directly with
  // no client-side blob/object-URL handling. See DocumentViewPage.js.
  printableUrl: (id) => `${API_URL}/api/documents/${id}/printable`,
  // Convert an uploaded Word/PDF/text file into a proposed document body.
  // Multipart, so uploadConfig() (300s) and NOT aiConfig() -- no model is
  // called here; the conversion is deterministic and the time is spent on the
  // upload itself. Returns the markdown for the author to look at; nothing is
  // stored server-side either way. See services/document_import_service.py.
  importPreview: (file, { documentId, hierarchyNodeId } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (documentId) formData.append('document_id', documentId);
    if (hierarchyNodeId) formData.append('hierarchy_node_id', hierarchyNodeId);
    return apiClient.post('/api/documents/import-preview', formData, uploadConfig({
      headers: { 'Content-Type': 'multipart/form-data' },
    }));
  },
  // Multi-attachment support (Phase 3) -- separate from the legacy single
  // `file`/download above, which stays as-is.
  // `revisionId` selects the proposed set for an open revision (staged files
  // plus the live ones) instead of the live set alone -- see documents.py's
  // list_attachments/add_attachments for the matching backend behavior.
  listAttachments: (id, revisionId) => apiClient.get(`/api/documents/${id}/attachments`, {
    params: revisionId ? { revision_id: revisionId } : {},
  }),
  addAttachments: (id, files, revisionId) => {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('files', file));
    if (revisionId) formData.append('revision_id', String(revisionId));
    return apiClient.post(`/api/documents/${id}/attachments`, formData, uploadConfig({
      headers: { 'Content-Type': 'multipart/form-data' },
    }));
  },
  downloadAttachment: (id, attachmentId) => apiClient.get(
    `/api/documents/${id}/attachments/${attachmentId}/download`, uploadConfig({ responseType: 'blob' })
  ),
  deleteAttachment: (id, attachmentId) => apiClient.delete(
    `/api/documents/${id}/attachments/${attachmentId}`
  ),

  // Contributors on ONE document (issue #132) -- similar to owner, but a
  // per-document row rather than a node-wide grant, and carrying explicit
  // read/comment/edit capabilities instead of implying "may do everything".
  listContributors: (id) => apiClient.get(`/api/documents/${id}/contributors`),
  addContributor: (id, payload) => apiClient.post(`/api/documents/${id}/contributors`, payload),
  removeContributor: (id, contributorId) => apiClient.delete(
    `/api/documents/${id}/contributors/${contributorId}`
  ),

  // Revisions: proposing a change to a policy that stays live and enforced
  // throughout. The document endpoints above always return the PUBLISHED
  // state -- a revision is only ever readable at its own URL.
  createRevision: (id, purpose = 'revision') =>
    apiClient.post(`/api/documents/${id}/revisions`, { purpose }),
  getCurrentRevision: (id) => apiClient.get(`/api/documents/${id}/revisions/current`),
  // Unlike getCurrentRevision, resolves to the SAME revision regardless of
  // whether it's still open -- what a fixed link (ApprovalsPage's "View
  // proposed changes") needs.
  getRevision: (id, revisionId) => apiClient.get(`/api/documents/${id}/revisions/${revisionId}`),
  updateRevision: (id, revisionId, data) =>
    apiClient.put(`/api/documents/${id}/revisions/${revisionId}`, data, uploadConfig({
      headers: { 'Content-Type': 'multipart/form-data' },
    })),
  convertReviewToRevision: (id, revisionId) =>
    apiClient.post(`/api/documents/${id}/revisions/${revisionId}/convert-to-revision`),
  withdrawRevision: (id, revisionId) =>
    apiClient.delete(`/api/documents/${id}/revisions/${revisionId}`),

  // Redline (tracked changes) -- computed on read, nothing here is ever
  // written back as document content. See services/redline_service.py.
  getRevisionRedline: (id, revisionId, baseline = 'published') =>
    apiClient.get(`/api/documents/${id}/revisions/${revisionId}/redline`, {
      params: { baseline },
    }),
  getDocumentRedline: (id) => apiClient.get(`/api/documents/${id}/redline`),
  // Backs the live "Changes" pane. Computes and stores nothing -- safe to call
  // on every keystroke (debounced by the caller).
  previewRevisionRedline: (id, revisionId, content, baseline = 'published') =>
    apiClient.post(`/api/documents/${id}/revisions/${revisionId}/redline/preview`,
      { content, baseline }),
  // Same idea for a plain draft document (GitHub issue #20) -- no `baseline`
  // choice here, unlike previewRevisionRedline: a never-published draft has
  // no submission/published baseline to pick between, so the backend always
  // diffs from the empty start of the draft's whole history.
  previewDocumentRedline: (id, content) =>
    apiClient.post(`/api/documents/${id}/redline/preview`, { content }),
  getVersionRedline: (id, versionId) =>
    apiClient.get(`/api/documents/${id}/versions/${versionId}/redline`),
  // What ONE save changed, derived from the version chain -- so every history
  // row can answer, not just the one row that froze a redline at publish.
  // Deliberately a second method rather than a flag on getVersionRedline: the
  // frozen redline covers a whole approved revision, this covers one save, and
  // a published revision's row legitimately has both.
  getVersionChanges: (id, versionId) =>
    apiClient.get(`/api/documents/${id}/versions/${versionId}/changes`),

  // Comments anchored to a passage. `revisionId` omitted means the document's
  // own thread; see services/comment_service.py for who may post one.
  listComments: (id, revisionId, includeResolved = true) =>
    apiClient.get(`/api/documents/${id}/comments`, {
      params: {
        ...(revisionId ? { revision_id: revisionId } : {}),
        include_resolved: includeResolved,
      },
    }),
  createComment: (id, data) => apiClient.post(`/api/documents/${id}/comments`, data),
  resolveComment: (id, commentId, unresolve = false) =>
    apiClient.post(`/api/documents/${id}/comments/${commentId}/resolve`, null, {
      params: unresolve ? { unresolve: true } : {},
    }),
  deleteComment: (id, commentId) =>
    apiClient.delete(`/api/documents/${id}/comments/${commentId}`),

  // @mentions (GitHub issue #75). `mentionable` backs the @ picker and lists
  // only people who can already READ this document -- being mentioned grants
  // nothing, so the list is exactly "who is already in the conversation".
  // It is an affordance; services/mention_service.py re-checks every id on the
  // POST, which is the control.
  mentionable: (id) => apiClient.get(`/api/documents/${id}/comments/mentionable`),
  // The Comments area of My Tasks. Self-scoped from the session, so there is no
  // user id to pass -- the same shape as myTasks above. Issue #190:
  // include_dismissed folds dismissed mentions in beside the live ones, the
  // same endpoint with a flag rather than a second one.
  myMentions: (params = {}) =>
    apiClient.get('/api/documents/my-mentions', { params: buildParams(params) }),
  dismissMention: (mentionId) =>
    apiClient.post(`/api/documents/my-mentions/${mentionId}/dismiss`),
  undismissMention: (mentionId) =>
    apiClient.post(`/api/documents/my-mentions/${mentionId}/undismiss`),

  // AI document-type evaluation (GitHub issue #34). Advisory only -- nothing
  // here blocks a submission. `revisionId` omitted means the live document.
  getTypeEvaluation: (id, revisionId) =>
    apiClient.get(`/api/documents/${id}/type-evaluation`, {
      params: revisionId ? { revision_id: revisionId } : {},
    }),
  runTypeEvaluation: (id, { revisionId, force = false } = {}) =>
    apiClient.post(`/api/documents/${id}/type-evaluation`, {
      revision_id: revisionId || null,
      force,
    }),
  // Corpus comparison (GitHub issue #35). `withNarrative` asks a model to
  // explain the matches; without it the response is still a complete result,
  // since the candidates and their similarity scores are computed by the
  // application, not by a model.
  getCorpusScan: (id, { kind = 'duplicate_scan', revisionId } = {}) =>
    apiClient.get(`/api/documents/${id}/corpus-scan`, {
      params: {
        kind,
        ...(revisionId ? { revision_id: revisionId } : {}),
      },
    }),
  runCorpusScan: (id, { kind = 'duplicate_scan', revisionId, force = false,
                        withNarrative = false } = {}) =>
    apiClient.post(`/api/documents/${id}/corpus-scan`, {
      kind,
      revision_id: revisionId || null,
      force,
      with_narrative: withNarrative,
    }),
  // Acknowledgement requirement (issue #64) -- config lives on documentsAPI
  // because a requirement is a property of a document, gated on
  // documents.update. The signing/reporting half is acknowledgementsAPI below.
  getAcknowledgementRequirement: (id) =>
    apiClient.get(`/api/documents/${id}/acknowledgement-requirement`),
  saveAcknowledgementRequirement: (id, data) =>
    apiClient.put(`/api/documents/${id}/acknowledgement-requirement`, data),
  deleteAcknowledgementRequirement: (id) =>
    apiClient.delete(`/api/documents/${id}/acknowledgement-requirement`),
  // Policy Guide (AI-assisted authoring). NOT keyed on a document id: drafting
  // happens on /documents/new, before a document exists -- which is exactly
  // why DocumentEvaluation.document_id is nullable for these kinds. Both use
  // aiConfig's longer budget; the 60s default times out a real generation.
  runPolicyGuideDraft: ({ prompt, documentTypeId, documentId, hierarchyNodeId,
                          regulatoryReferenceIds = [], title } = {}) =>
    apiClient.post('/api/documents/policy-guide/draft', {
      prompt,
      document_type_id: documentTypeId,
      document_id: documentId || null,
      hierarchy_node_id: hierarchyNodeId || null,
      regulatory_reference_ids: regulatoryReferenceIds,
      title: title || null,
    }, aiConfig()),
  runPolicyGuideCheck: ({ content, regulatoryReferenceIds = [], focus, documentId,
                          revisionId, hierarchyNodeId, force = false } = {}) =>
    apiClient.post('/api/documents/policy-guide/check', {
      content,
      regulatory_reference_ids: regulatoryReferenceIds,
      focus: focus || null,
      document_id: documentId || null,
      revision_id: revisionId || null,
      hierarchy_node_id: hierarchyNodeId || null,
      force,
    }, aiConfig()),
  // The most recent conformance check (issue #150, stage 1) -- 200 with null
  // when nothing has run yet, same shape as getTypeEvaluation above. No
  // aiConfig(): this reads a stored row, it never calls a model.
  getConformanceCheck: (id, revisionId) =>
    apiClient.get(`/api/documents/${id}/conformance-check`, {
      params: revisionId ? { revision_id: revisionId } : {},
    }),
  // Issue #115. Unlike the two above this one DOES need a document id -- there
  // is nothing to scan until a policy exists -- and it is the one Drafting
  // Assistant call that runs against a live ACTIVE policy, via the open
  // revision or periodic review named in revisionId.
  runPolicyGuideCitations: ({ content, documentId, revisionId,
                              regulatoryReferenceIds = [], focus, force = false } = {}) =>
    apiClient.post('/api/documents/policy-guide/cite', {
      content,
      // `|| null` for symmetry with the two siblings above (issue #164). The
      // backend requires a real int here, so this only makes the refusal
      // consistent -- what actually stops the bad request is that
      // DraftingAssistantPanel does not offer the cite tab without a document.
      document_id: documentId || null,
      revision_id: revisionId || null,
      regulatory_reference_ids: regulatoryReferenceIds,
      focus: focus || null,
      force,
    }, aiConfig()),
  // Deterministic -- no model, so the ordinary 60s budget is right here.
  getReferenceCurrency: (id, { revisionId } = {}) =>
    apiClient.get(`/api/documents/${id}/reference-currency`, {
      params: { ...(revisionId ? { revision_id: revisionId } : {}) },
    }),
  listEvaluations: (id, { kind, revisionId } = {}) =>
    apiClient.get(`/api/documents/${id}/evaluations`, {
      params: {
        ...(kind ? { kind } : {}),
        ...(revisionId ? { revision_id: revisionId } : {}),
      },
    }),
};

// Hierarchy API
export const hierarchyAPI = {
  getLevels: () => apiClient.get('/api/hierarchy/levels'),
  getNodes: (params = {}) => apiClient.get('/api/hierarchy/nodes', { params }),
  getNode: (id) => apiClient.get(`/api/hierarchy/nodes/${id}`),
  createNode: (data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('hierarchy_level_id', String(data.hierarchy_level_id));
    if (data.parent_id) {
      formData.append('parent_id', String(data.parent_id));
    }
    
    return apiClient.post('/api/hierarchy/nodes', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  createLevel: (data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.description) {
      formData.append('description', data.description);
    }

    return apiClient.post('/api/hierarchy/levels', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteNode: (id) => apiClient.delete(`/api/hierarchy/nodes/${id}`),
};

// Approvals API
export const approvalsAPI = {
  getPending: (params = {}) => apiClient.get('/api/approvals/pending', { params }),
  approve: (stepInstanceId, data) => apiClient.post(`/api/approvals/instances/${stepInstanceId}/approve`, data),
  reject: (stepInstanceId, data) => apiClient.post(`/api/approvals/instances/${stepInstanceId}/reject`, data),
};

// Workflows API (approval groups/members, workflow templates, submission)
// Every group and workflow has exactly one owning hierarchy node (hierarchy_node_id) and
// applies to that node and everything beneath it -- there is no separate assignment step.
export const workflowsAPI = {
  // No params = every group company-wide. visibleFromNodeId = owned at that node or any
  // ancestor of it (for gate-builder pickers). ownedByNodeId = owned at exactly that node
  // (for a node's "defined here" list). Passing both is invalid -- pick one helper.
  getGroups: (params = {}) => apiClient.get('/api/workflows/groups', { params }),
  getGroupsVisibleFrom: (nodeId) =>
    apiClient.get('/api/workflows/groups', { params: { visible_from_node_id: nodeId } }),
  getGroupsOwnedBy: (nodeId) =>
    apiClient.get('/api/workflows/groups', { params: { owned_by_node_id: nodeId } }),
  createGroup: (data) => apiClient.post('/api/workflows/groups', data),
  updateGroup: (id, data) => apiClient.patch(`/api/workflows/groups/${id}`, data),
  deleteGroup: (id) => apiClient.delete(`/api/workflows/groups/${id}`),
  getGroupMembers: (groupId) => apiClient.get(`/api/workflows/groups/${groupId}/members`),
  // Members are Users picked from the directory, not free-typed name/email, so
  // one person has one identity across the system. Re-adding someone previously
  // removed reactivates their original row (preserving audit FKs).
  addGroupMember: (groupId, userId) =>
    apiClient.post(`/api/workflows/groups/${groupId}/members`, { user_id: userId }),
  deactivateGroupMember: (groupId, memberId) =>
    apiClient.delete(`/api/workflows/groups/${groupId}/members/${memberId}`),

  getTemplates: (params = {}) => apiClient.get('/api/workflows/templates', { params }),
  getTemplatesOwnedBy: (nodeId) =>
    apiClient.get('/api/workflows/templates', { params: { owned_by_node_id: nodeId } }),
  getTemplate: (id) => apiClient.get(`/api/workflows/templates/${id}`),
  createTemplate: (data) => apiClient.post('/api/workflows/templates', data),
  updateTemplate: (id, data) => apiClient.patch(`/api/workflows/templates/${id}`, data),
  deleteTemplate: (id) => apiClient.delete(`/api/workflows/templates/${id}`),

  setTemplateSteps: (id, steps) => apiClient.put(`/api/workflows/templates/${id}/steps`, { steps }),

  // documentId is optional -- when given, the response flags the template
  // that document's most recent workflow used (is_last_used), so the picker
  // can auto-select it.
  getApplicableTemplates: (hierarchyNodeId, documentId) =>
    apiClient.get('/api/workflows/templates/applicable', {
      params: { hierarchy_node_id: hierarchyNodeId, document_id: documentId },
    }),

  submitForApproval: (docId, templateId) =>
    apiClient.post(`/api/workflows/documents/${docId}/submit`, templateId ? { template_id: templateId } : {}),

  // A revision (or periodic review) approves separately from the document --
  // see routers/workflows.py::submit_revision_for_approval. Never touches
  // document.status; the policy stays live throughout.
  submitRevisionForApproval: (revisionId, templateId) =>
    apiClient.post(`/api/workflows/revisions/${revisionId}/submit`,
      templateId ? { template_id: templateId } : {}),
};

// Regulatory References API (lookup table, global -- not hierarchy-scoped)
export const regulatoryReferencesAPI = {
  getAll: (params = {}) => apiClient.get('/api/regulatory-references/', { params }),
  create: (data) => apiClient.post('/api/regulatory-references/', data),
  update: (id, data) => apiClient.patch(`/api/regulatory-references/${id}`, data),
  delete: (id) => apiClient.delete(`/api/regulatory-references/${id}`),

  // The source library: the ACTUAL TEXT of a standard, which is what grounds
  // the Policy Guide. Methods on this existing object rather than a new API
  // object, because a loaded source is a property of a regulatory reference --
  // the same reasoning that puts the endpoints on that router.
  //
  // withSources() is the only one an ordinary author calls (it backs the
  // Policy Guide's picker) and is the only one gated on a session alone; the
  // other three need regulatory_references read/update.
  withSources: () => apiClient.get('/api/regulatory-references/with-sources'),
  // Any signed-in author/approver, not just regulatory_references.read
  // holders -- see the endpoint's own docstring (issue #150/#22's trap). This
  // is what RegulatoryReferenceMultiSelect uses on the Documents filter bar
  // and DocumentForm; getAll() above stays the admin-list call.
  getSelectable: () => apiClient.get('/api/regulatory-references/selectable'),
  // The Source Library admin index (issue #102): PAGED, and carrying counts
  // rather than clause bodies. The screen used to build this shape itself by
  // calling getSource() once per reference -- 157 requests and ~4.3 MB per page
  // load once the eCFR import landed.
  sourceLibrary: (params) => apiClient.get('/api/regulatory-references/source-library', { params }),
  // One edition's clauses, paged. getSource() below still returns ALL of them
  // because the editor replaces a whole edition and therefore has to hold them
  // all; this is for reading, where 107 clauses in one response is the problem.
  getSourceExcerpts: (id, params) => apiClient.get(
    `/api/regulatory-references/${id}/source/excerpts`, { params }),
  getSource: (id) => apiClient.get(`/api/regulatory-references/${id}/source`),
  getSourceHistory: (id) => apiClient.get(`/api/regulatory-references/${id}/source/history`),
  saveSource: (id, data) => apiClient.put(`/api/regulatory-references/${id}/source`, data),
};

// Regulatory Categories API (how a reference is classified -- "Accreditation",
// "Legal - Federal"). Same simple name/description lookup shape as policy areas.
export const regulatoryCategoriesAPI = {
  getAll: (params = {}) => apiClient.get('/api/regulatory-categories/', { params }),
  create: (data) => apiClient.post('/api/regulatory-categories/', data),
  update: (id, data) => apiClient.patch(`/api/regulatory-categories/${id}`, data),
  delete: (id) => apiClient.delete(`/api/regulatory-categories/${id}`),
  // require_identity alone, not regulatory_categories.read -- issue #22/#150's
  // trap, applied here (issue #222) so ReviewFrequencyModal's category picker
  // doesn't need a DIFFERENT resource grant from the one that already gates
  // opening that modal (review_frequencies.create/update).
  getSelectable: () => apiClient.get('/api/regulatory-categories/selectable'),
};

// Change Categories API (why a document was sent back, plus the organizational
// rule for what its send-back target is constrained to -- see models.SendBackRule).
export const changeCategoriesAPI = {
  getAll: (params = {}) => apiClient.get('/api/change-categories/', { params }),
  create: (data) => apiClient.post('/api/change-categories/', data),
  update: (id, data) => apiClient.patch(`/api/change-categories/${id}`, data),
  delete: (id) => apiClient.delete(`/api/change-categories/${id}`),
  // require_identity alone (issue #150/#22's trap) -- what RejectModal's
  // category dropdown uses, carrying send_back_rule so the send-back rules
  // it branches on keep working for a node-scoped approver.
  getSelectable: () => apiClient.get('/api/change-categories/selectable'),
};

// Document Templates API (pre-authored starting bodies -- Policy/Procedure/
// Job Aid -- picked on document create to pre-populate the content editor).
export const documentTemplatesAPI = {
  getAll: (params = {}) => apiClient.get('/api/document-templates/', { params }),
  // Any signed-in author, not just document_templates.read holders -- see
  // GET /api/document-templates/selectable's own docstring. This is what
  // DocumentForm's New Document picker uses; getAll() above stays the
  // admin-list call for the Document Templates admin screen.
  getSelectable: () => apiClient.get('/api/document-templates/selectable'),
  create: (data) => apiClient.post('/api/document-templates/', data),
  update: (id, data) => apiClient.patch(`/api/document-templates/${id}`, data),
  delete: (id) => apiClient.delete(`/api/document-templates/${id}`),
};

// Document Types API -- what KIND of document (Policy/Procedure/Job Aid),
// split from Document Templates (GitHub issue #21). getAll() is the admin-list
// call (document_types.read); getSelectable() is what DocumentForm's type
// picker and DocumentTemplateModal's type select actually use, needing only a
// session -- same document_templates.read-is-org-wide-only trap #22 already
// found, closed here before it could recur under a new resource name.
export const documentTypesAPI = {
  getAll: (params = {}) => apiClient.get('/api/document-types/', { params: buildParams(params) }),
  getSelectable: () => apiClient.get('/api/document-types/selectable'),
  create: (data) => apiClient.post('/api/document-types/', data),
  update: (id, data) => apiClient.patch(`/api/document-types/${id}`, data),
  delete: (id) => apiClient.delete(`/api/document-types/${id}`),

  // Rubrics (GitHub issue #34) -- what a document of this type must contain.
  // saveRubric always creates a NEW version; there is no in-place edit.
  getRubric: (typeId) => apiClient.get(`/api/document-types/${typeId}/rubric`),
  getRubricHistory: (typeId) => apiClient.get(`/api/document-types/${typeId}/rubric/history`),
  saveRubric: (typeId, data) => apiClient.put(`/api/document-types/${typeId}/rubric`, data),
};

// Review Frequencies API (lookup table tied to a Regulatory Reference).
// regulatory_reference_id may be a single id or an array -- buildParams expands
// either into repeated bare query params for the cascading document dropdown.
export const reviewFrequenciesAPI = {
  getAll: (params = {}) => apiClient.get('/api/review-frequencies/', { params: buildParams(params) }),
  create: (data) => apiClient.post('/api/review-frequencies/', data),
  update: (id, data) => apiClient.patch(`/api/review-frequencies/${id}`, data),
  delete: (id) => apiClient.delete(`/api/review-frequencies/${id}`),
  // require_identity alone (issue #150/#22's trap) -- what DocumentForm's
  // cascade (pick references, then filter frequencies to those) uses. Takes
  // the same repeatable regulatory_reference_id filter as getAll, PLUS
  // regulatory_category_id (issue #222). NOTE THE ASYMMETRY: here a
  // regulatory_reference_id filter is WIDENED server-side to also return
  // frequencies scoped to those references' CATEGORIES; getAll's identically
  // named filter is NOT widened and answers exactly what is stored against
  // that id (the admin list's job) -- see routers/review_frequencies.py's
  // own docstring on why the two deliberately disagree.
  getSelectable: (params = {}) => apiClient.get('/api/review-frequencies/selectable', { params: buildParams(params) }),
};

// Policy Areas API (cross-cutting subject categories, independent of the hierarchy)
export const policyAreasAPI = {
  getAll: (params = {}) => apiClient.get('/api/policy-areas/', { params: buildParams(params) }),
  create: (data) => apiClient.post('/api/policy-areas/', data),
  update: (id, data) => apiClient.patch(`/api/policy-areas/${id}`, data),
  delete: (id) => apiClient.delete(`/api/policy-areas/${id}`),
  // require_identity alone (issue #150/#22's trap) -- what
  // PolicyAreaMultiSelect uses on the Documents filter bar; getAll() above
  // stays the admin-list call.
  getSelectable: () => apiClient.get('/api/policy-areas/selectable'),
};

// Applicability Groups API (reusable named sets of hierarchy locations).
// Membership is replaced wholesale via replaceMembers, never patched piecemeal.
export const applicabilityGroupsAPI = {
  getAll: (params = {}) => apiClient.get('/api/applicability-groups/', { params: buildParams(params) }),
  get: (id) => apiClient.get(`/api/applicability-groups/${id}`),
  create: (data) => apiClient.post('/api/applicability-groups/', data),
  update: (id, data) => apiClient.patch(`/api/applicability-groups/${id}`, data),
  delete: (id) => apiClient.delete(`/api/applicability-groups/${id}`),
  replaceMembers: (id, { nodes = [], hierarchy_level_ids = [] }) =>
    apiClient.put(`/api/applicability-groups/${id}/members`, { nodes, hierarchy_level_ids }),
  // require_identity alone (issue #150/#22's trap) -- what DocumentForm's
  // ApplicabilityGroupMultiSelect uses; getAll() above stays the admin-list
  // call.
  getSelectable: () => apiClient.get('/api/applicability-groups/selectable'),
};

// Dashboard API (status counts + discrete coming-due / expired bands)
export const dashboardAPI = {
  // scope: 'mine' | 'all' (issue #17) -- must match whatever documentsAPI.list
  // was called with on the same page, or the tiles and the list they link to
  // disagree. Omit entirely for the old unscoped behaviour.
  getStats: (params = {}) => apiClient.get('/api/dashboard/stats', { params: buildParams(params) }),
};

// Users / Roles API.
//
// A role assignment is SCOPED: hierarchy_node_id null means organization-wide, a
// node id means that node and everything beneath it. Because the same role can be
// held at several nodes, removing one is keyed by the ASSIGNMENT id
// (unassignAssignment) -- unassignRole is the older, ambiguous form, which the
// backend documents as targeting only the organization-wide grant.
export const usersAPI = {
  getUsers: (params = {}) => apiClient.get('/api/users', { params: buildParams(params) }),
  // The Users admin screen -- SEARCHED and PAGED (issue #138). A separate
  // route from getUsers above, not a shape change to it: GroupMembersPanel
  // needs every active user at once to pick committee members, and an
  // envelope would break that. Same relationship sourceLibrary has to the
  // plain regulatory-reference list.
  directory: (params = {}) => apiClient.get('/api/users/directory', { params: buildParams(params) }),
  // Narrow, any-authenticated-user picker (owner assignment) -- distinct from
  // getUsers above, which needs users.read. See routers/users.py.
  selectable: () => apiClient.get('/api/users/selectable'),
  getUser: (id) => apiClient.get(`/api/users/${id}`),
  createUser: (data) => apiClient.post('/api/users', data),
  updateUser: (id, data) => apiClient.patch(`/api/users/${id}`, data),
  setPassword: (id, newPassword) => apiClient.post(`/api/users/${id}/set-password`, { new_password: newPassword }),
  assignRole: (userId, { role_id, hierarchy_node_id = null }) =>
    apiClient.post(`/api/users/${userId}/roles`, { role_id, hierarchy_node_id }),
  unassignAssignment: (userId, assignmentId) =>
    apiClient.delete(`/api/users/${userId}/role-assignments/${assignmentId}`),
  unassignRole: (userId, roleId) => apiClient.delete(`/api/users/${userId}/roles/${roleId}`),

  getRoles: () => apiClient.get('/api/roles'),
  // The permission matrix's rows, served from the backend Resource enum so the
  // UI never hardcodes (and drifts from) the resource list.
  getResources: () => apiClient.get('/api/roles/resources'),
  // The matrix's COLUMNS, same reasoning as getResources above for the rows.
  // This existed on the backend but was never actually called -- RoleModal
  // rendered its own hardcoded column list regardless, which is how a role
  // could never be granted users.impersonate through the UI at all: that action
  // was added to the backend enum but never to the frontend's copy.
  getActions: () => apiClient.get('/api/roles/actions'),
  createRole: (data) => apiClient.post('/api/roles', data),
  // Passing `permissions` here replaces the whole matrix in the SAME request, so
  // a role save can't half-apply the way two sequential calls could.
  updateRole: (id, data) => apiClient.patch(`/api/roles/${id}`, data),
  deleteRole: (id) => apiClient.delete(`/api/roles/${id}`),
  replacePermissions: (roleId, permissions) =>
    apiClient.put(`/api/roles/${roleId}/permissions`, { permissions }),

  // Default/home locations (issue #17) -- admin setting them for SOMEONE ELSE.
  // `nodes` is [{hierarchy_node_id, include_descendants}], same shape
  // HierarchyNodeTreeMultiSelect's selection already produces. Declarative
  // full replace, same pattern as replacePermissions above.
  getHomeNodes: (userId) => apiClient.get(`/api/users/${userId}/home-nodes`),
  setHomeNodes: (userId, nodes) =>
    apiClient.put(`/api/users/${userId}/home-nodes`, { nodes }),

  // Delegations (issue #44) -- admin setting up cover on SOMEONE ELSE's behalf.
  // Note these are gated harder than the home-node pair above: users.impersonate
  // plus the permission-subset rule, not users.update, because a delegation is
  // standing authority rather than a view preference. See routers/users.py.
  getDelegations: (userId) => apiClient.get(`/api/users/${userId}/delegations`),
  createDelegation: (userId, data) =>
    apiClient.post(`/api/users/${userId}/delegations`, data),
  revokeDelegation: (userId, delegationId) =>
    apiClient.delete(`/api/users/${userId}/delegations/${delegationId}`),
};

// Bookmarks (issue #59) -- always self-scoped, no admin-facing counterpart:
// unlike home-nodes/delegations above, a bookmark is purely personal, so
// there is nothing for an admin to set up on someone else's behalf.
export const bookmarksAPI = {
  list: () => apiClient.get('/api/bookmarks/'),
  create: (documentId, folderId = null) =>
    apiClient.post('/api/bookmarks/', { document_id: documentId, folder_id: folderId }),
  move: (bookmarkId, folderId) =>
    apiClient.patch(`/api/bookmarks/${bookmarkId}`, { folder_id: folderId }),
  remove: (bookmarkId) => apiClient.delete(`/api/bookmarks/${bookmarkId}`),

  listFolders: () => apiClient.get('/api/bookmarks/folders'),
  createFolder: (name) => apiClient.post('/api/bookmarks/folders', { name }),
  renameFolder: (folderId, name) =>
    apiClient.patch(`/api/bookmarks/folders/${folderId}`, { name }),
  deleteFolder: (folderId) => apiClient.delete(`/api/bookmarks/folders/${folderId}`),
};

// Policy use analytics (issue #62) -- reports only; capture happens
// server-side inside the endpoints that already serve a search or a document,
// so there is nothing for the frontend to post. A wholly new object rather
// than methods on an existing one, since analytics has no counterpart surface
// to hang off -- the same reason bookmarksAPI above is its own object.
export const analyticsAPI = {
  getSearches: (params = {}) => apiClient.get('/api/analytics/searches', { params }),
  getFilters: (params = {}) => apiClient.get('/api/analytics/filters', { params }),
  getDocuments: (params = {}) => apiClient.get('/api/analytics/documents', { params }),
  // Usage over time. Rendered as a bucket table today; also the exact shape a
  // trend chart would need, so that work stays frontend-only when it happens.
  getTrend: (params = {}) => apiClient.get('/api/analytics/trend', { params }),
};

// Document Insights (issue #125) -- the policy likeness log and the coverage
// gap report. Its own object for the same reason bookmarksAPI and analyticsAPI
// are: there is no existing surface these belong on. Note NONE of these calls
// pass aiConfig() -- everything here is deterministic and calls no model. The
// one endpoint that does (the conflict scan) sets its own timeout below.
export const insightsAPI = {
  getLikeness: (params = {}) => apiClient.get('/api/insights/likeness', { params }),
  getPair: (id) => apiClient.get(`/api/insights/likeness/${id}`),
  triage: (id, data) => apiClient.post(`/api/insights/likeness/${id}/triage`, data),
  rebuild: (data = {}) => apiClient.post('/api/insights/rebuild', data),
  getCoverage: () => apiClient.get('/api/insights/coverage'),
  // The only AI-backed call here: it sends two whole policy bodies, so it gets
  // the long budget the Drafting Assistant's calls use. The 60s default would
  // time out a real run, and the server allows a repair retry on top.
  runConflictScan: (id, data = {}) =>
    apiClient.post(`/api/insights/likeness/${id}/conflict-scan`, data, aiConfig()),

  // Consolidation (issue #111). Only `consolidate` reaches a model -- it sends
  // up to five whole policy bodies and gets a whole policy back, so it is the
  // largest call in the app in both directions and takes the long budget.
  // Re-opening a saved run is free, which is why getConsolidation does not.
  getConsolidationCandidates: (likenessId) =>
    apiClient.get(`/api/insights/likeness/${likenessId}/consolidation-candidates`),
  consolidate: (data) => apiClient.post('/api/insights/consolidate', data, aiConfig()),
  getConsolidation: (evaluationId) =>
    apiClient.get(`/api/insights/consolidations/${evaluationId}`),
  markConsolidated: (evaluationId, data = {}) =>
    apiClient.post(`/api/insights/consolidations/${evaluationId}/accepted`, data),
};

// Health check
// Auth API
export const authAPI = {
  // Never rejects on "not logged in": /api/auth/me answers 200 with
  // authenticated=false, which is a normal answer, not an error.
  me: () => apiClient.get('/api/auth/me'),
  login: (email, password) => apiClient.post('/api/auth/login', { email, password }),
  logout: () => apiClient.post('/api/auth/logout'),
  changePassword: (currentPassword, newPassword) =>
    apiClient.post('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  impersonate: (userId) => apiClient.post(`/api/auth/impersonate/${userId}`),
  stopImpersonating: () => apiClient.post('/api/auth/impersonate/stop'),

  // Default/home locations (issue #17) -- self-service form of
  // usersAPI.getHomeNodes/setHomeNodes above, for the signed-in user setting
  // their OWN, from /profile. No users.* permission needed on the backend.
  // Email notification preferences (issue #76). Self-service only -- there is
  // no admin counterpart, unlike home-nodes and delegations below: nobody sets
  // somebody else's. Every category defaults to OFF server-side.
  getMyNotificationPreferences: () =>
    apiClient.get('/api/auth/me/notification-preferences'),
  setMyNotificationPreferences: (data) =>
    apiClient.put('/api/auth/me/notification-preferences', data),

  getMyHomeNodes: () => apiClient.get('/api/auth/me/home-nodes'),
  setMyHomeNodes: (nodes) => apiClient.put('/api/auth/me/home-nodes', { nodes }),

  // Delegations (issue #44), self-service: lending my own authority, seeing what
  // has been lent to me, and the act-as switch itself.
  getMyDelegations: () => apiClient.get('/api/auth/me/delegations'),
  createMyDelegation: (data) => apiClient.post('/api/auth/me/delegations', data),
  revokeMyDelegation: (delegationId) =>
    apiClient.delete(`/api/auth/me/delegations/${delegationId}`),
  getDelegationsToMe: () => apiClient.get('/api/auth/me/delegations/received'),
  // Takes a DELEGATION id, never a user id: the server re-derives whose
  // authority, which capabilities and for how long from the row.
  startDelegation: (delegationId) =>
    apiClient.post(`/api/auth/delegation/${delegationId}`),
  stopDelegation: () => apiClient.post('/api/auth/delegation/stop'),
};

// Policy acknowledgements (issue #64). A wholly new object rather than methods
// on an existing one, the same reasoning bookmarksAPI and analyticsAPI each got
// one: there is no existing object both the self-service and the reporting
// halves belong on. The requirement CONFIG, by contrast, hangs off documentsAPI
// below, because a requirement is a property of a document.
export const acknowledgementsAPI = {
  // Self-service -- require_identity only, no permission needed.
  mine: () => apiClient.get('/api/acknowledgements/mine'),
  forDocument: (documentId) =>
    apiClient.get(`/api/acknowledgements/documents/${documentId}/mine`),
  acknowledge: (documentId) =>
    apiClient.post('/api/acknowledgements/', { document_id: documentId }),

  // Reporting -- gated on the `acknowledgements` resource, org-wide only.
  status: () => apiClient.get('/api/acknowledgements/status'),
  roster: (documentId) => apiClient.get(`/api/acknowledgements/status/${documentId}`),
};

// Non-secret render hints the server owns (currently the inactivity thresholds).
// Unauthenticated on purpose -- see backend routers/config.py. Kept separate from
// authAPI because nothing here varies by caller.
export const configAPI = {
  client: () => apiClient.get('/api/config/client'),
  // Admin-gated (settings.read/settings.update) -- GitHub issue #158.
  getSettings: () => apiClient.get('/api/config/settings'),
  updateSettings: (values) => apiClient.put('/api/config/settings', { values }),
  // The AI usage report (issue #165). settings.read-gated, so it rides on the
  // same grant that renders the quota editor beside it. No aiConfig(): this
  // reads counters, it never calls a model.
  getAiUsage: (params = {}) => apiClient.get('/api/config/ai-usage', { params }),
};

export const health = () => apiClient.get('/health');

export default apiClient;
